import type {
  ChartDatum,
  GmfApplicationSummary,
  GmfDashboardMetrics,
  GmfDeveloperPipelineFormValues,
  GmfMapProject,
} from '../types/gmfDeveloperPipeline.types';

export const parseMetricNumber = (value: unknown): number => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (typeof value !== 'string') return 0;

  const parsed = Number(value.replace(/[$,\s]/g, ''));
  return Number.isFinite(parsed) ? parsed : 0;
};

const increment = (counts: Map<string, number>, key: unknown, amount = 1) => {
  if (typeof key !== 'string' || key.trim().length === 0) return;
  const normalizedKey = key.trim();
  counts.set(normalizedKey, (counts.get(normalizedKey) ?? 0) + amount);
};

const toChartData = (counts: Map<string, number>): ChartDatum[] =>
  Array.from(counts.entries())
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);

const isGmfFormData = (value: GmfApplicationSummary['applicationData']): value is Partial<GmfDeveloperPipelineFormValues> =>
  !!value && typeof value === 'object';

const zipNeighborhoods: Record<string, string> = {
  '53204': 'Walker Point',
  '53205': 'Lindsay Heights',
  '53206': 'Harambee',
  '53208': 'Washington Park',
  '53210': 'Metcalfe Park',
  '53212': 'Bronzeville',
};

const getNeighborhoodName = (zipCode: unknown) => {
  if (typeof zipCode !== 'string' || zipCode.trim().length === 0) return 'Unknown';
  return zipNeighborhoods[zipCode.trim()] ?? `ZIP ${zipCode.trim()}`;
};

const getPipelineStage = (projectDetails: Partial<GmfDeveloperPipelineFormValues['projectDetails']> | undefined) => {
  if (!projectDetails) return 'Early Stage';

  const siteControlStatus = projectDetails.siteControlStatus ?? '';
  const projectByRight = projectDetails.projectByRight ?? '';
  const hasFinancingStrategy =
    typeof projectDetails.preliminaryFinancingStrategy === 'string' && projectDetails.preliminaryFinancingStrategy.trim().length > 0;
  const hasPredevelopmentNeed = parseMetricNumber(projectDetails.preDevelopmentCostEstimate) > 0;

  if (projectByRight === 'Yes' && ['Owned', 'Under Contract'].includes(siteControlStatus)) return 'Construction Ready';
  if (hasFinancingStrategy) return 'Financing';
  if (['LOI Obtained', 'Under Contract', 'Owned'].includes(siteControlStatus)) return 'Site Control';
  if (hasPredevelopmentNeed) return 'Predevelopment';
  return 'Early Stage';
};

const mapCoordinates = (index: number): Pick<GmfMapProject, 'x' | 'y'> => {
  const columns = 4;
  const column = index % columns;
  const row = Math.floor(index / columns) % 3;
  return {
    x: 185 + column * 72 + row * 18,
    y: 165 + row * 78 + column * 16,
  };
};

export const buildGmfDashboardMetrics = (applications: GmfApplicationSummary[] = []): GmfDashboardMetrics => {
  const developers = new Set<string>();
  const zipCodes = new Map<string, number>();
  const neighborhoodUnits = new Map<string, number>();
  const pipelineStages = new Map<string, number>();
  const siteControl = new Map<string, number>();
  const constructionTypes = new Map<string, number>();
  const acreGraduate = new Map<string, number>();
  const minorityOwned = new Map<string, number>();
  const neighborhoodBased = new Map<string, number>();
  const organizationTypes = new Map<string, number>();
  const mapProjects: GmfMapProject[] = [];

  let totalProposedUnits = 0;
  let totalDevelopmentCost = 0;
  let totalPreDevelopmentNeed = 0;
  let totalAcquisitionCost = 0;

  applications.forEach((application, index) => {
    const applicationData = application.applicationData;
    if (!isGmfFormData(applicationData)) return;

    const projectDetails = applicationData.projectDetails;
    const applicantInformation = applicationData.applicantInformation;
    const proposedUnits = parseMetricNumber(projectDetails?.proposedUnits);

    const developerKey =
      applicantInformation?.contactPersonEmail?.trim().toLowerCase() ||
      application.contactEmail?.trim().toLowerCase() ||
      applicantInformation?.entityName?.trim().toLowerCase() ||
      application.id;
    developers.add(developerKey);

    totalProposedUnits += proposedUnits;
    totalDevelopmentCost += parseMetricNumber(projectDetails?.developmentCostEstimate);
    totalPreDevelopmentNeed += parseMetricNumber(projectDetails?.preDevelopmentCostEstimate);
    totalAcquisitionCost += parseMetricNumber(projectDetails?.acquisitionCostEstimate);

    increment(zipCodes, projectDetails?.zipCode);
    increment(neighborhoodUnits, getNeighborhoodName(projectDetails?.zipCode), proposedUnits);
    increment(pipelineStages, getPipelineStage(projectDetails));
    increment(siteControl, projectDetails?.siteControlStatus);
    increment(constructionTypes, projectDetails?.constructionType);
    increment(acreGraduate, applicantInformation?.acreGraduate);
    increment(organizationTypes, applicantInformation?.organizationType);

    const identities = applicantInformation?.applicantIdentity ?? [];
    increment(minorityOwned, identities.includes('Minority-Owned Developer') ? 'Minority-Owned' : 'Other');
    increment(neighborhoodBased, identities.includes('Neighborhood-Based Organization') ? 'Neighborhood-Based' : 'Other');
    if (projectDetails) {
      mapProjects.push({
        id: application.id,
        name: applicantInformation?.entityName || application.contactName || 'Pipeline Project',
        address: projectDetails.streetAddress || 'Address not provided',
        zipCode: projectDetails.zipCode || '',
        units: proposedUnits,
        ...mapCoordinates(index),
      });
    }
  });

  const totalProjects = applications.length;
  const averageUnits = totalProjects > 0 ? totalProposedUnits / totalProjects : 0;
  const averageDevelopmentCost = totalProjects > 0 ? totalDevelopmentCost / totalProjects : 0;
  const estimatedCostPerUnit = totalProposedUnits > 0 ? totalDevelopmentCost / totalProposedUnits : 0;

  return {
    totalDevelopers: developers.size,
    totalProjects,
    totalProposedUnits,
    totalDevelopmentCost,
    totalPreDevelopmentNeed,
    totalAcquisitionCost,
    averageUnits,
    averageDevelopmentCost,
    estimatedCostPerUnit,
    zipCodeProjects: toChartData(zipCodes),
    neighborhoodUnits: toChartData(neighborhoodUnits),
    pipelineStages: toChartData(pipelineStages),
    siteControlStatus: toChartData(siteControl),
    constructionTypes: toChartData(constructionTypes),
    acreGraduate: toChartData(acreGraduate),
    minorityOwned: toChartData(minorityOwned),
    neighborhoodBased: toChartData(neighborhoodBased),
    organizationTypes: toChartData(organizationTypes),
    mapProjects,
  };
};
